// Story 11.12 (ADO-1963), T1 (AC2): whether a product is cheaper for the store on WOW.
//
// THE SHARED CONTRACT WITH STORY 11.5 (ADO-1956). The Cheaper-on-WOW badge (11.5) and the action
// that acts on it (11.12) answer the same question the same way, so both call `compareWithWow`
// and neither re-implements it. It is pure: no store, no request, no clock.
//
// The rule, unit price against unit price:
//   - cheaper only when both sides are priced, the WOW side's kind is `wow`, the two currencies
//     are the same (compared case-insensitively, as `moneyFormatterFor` keys them) and the WOW
//     unit price is strictly below the active one;
//   - equal, dearer, either side unpriced or unread (`undefined`), a currency mismatch, or a WOW
//     side of any other kind are all "not cheaper".
// Deposits are not part of it: they are the same per product and totalled apart (Epic 11's
// cross-cutting criteria). The comparison is WOW only (AC2): there is no National Distribution
// Centre or Value Centre comparison anywhere.
//
// The active side comes from the session's active entry (the PDP reads it from the shared price
// store, `comparableFromPriceEntry`), the WOW side from an isolated read at the WOW entry
// (`useWowOffer`). A caller passes `PriceQuoteDto`s as the price-quotes read returns them.

import type { PriceKind, PriceQuoteDto } from '../api/price-quotes.client';
import type { MoneyValue } from '../shared/money';
import type { PriceEntry } from './usePriceStore';

/** The part of a quote the comparison reads. Every `PriceQuoteDto` is one. */
export type ComparableQuote =
  | { readonly status: 'priced'; readonly unitPrice: MoneyValue; readonly kind: PriceKind }
  | { readonly status: 'unpriced' };

/** The answer: not cheaper, or cheaper with the two unit prices compared. */
export type WowComparison =
  | { readonly cheaper: false }
  | {
      readonly cheaper: true;
      readonly activeUnitPrice: MoneyValue;
      readonly wowUnitPrice: MoneyValue;
    };

const NOT_CHEAPER: WowComparison = { cheaper: false };

function sameCurrency(left: MoneyValue, right: MoneyValue): boolean {
  return left.currency.trim().toUpperCase() === right.currency.trim().toUpperCase();
}

/**
 * Whether `wow` (the product's quote at the WOW entry) is cheaper than `active` (its quote at
 * the session's active entry). `undefined` on either side is a quote not read (or not usable),
 * which is never cheaper.
 */
export function compareWithWow(
  active: ComparableQuote | PriceQuoteDto | undefined,
  wow: ComparableQuote | PriceQuoteDto | undefined,
): WowComparison {
  if (active?.status !== 'priced' || wow?.status !== 'priced') return NOT_CHEAPER;
  if (wow.kind !== 'wow') return NOT_CHEAPER;
  if (!sameCurrency(active.unitPrice, wow.unitPrice)) return NOT_CHEAPER;
  if (!(wow.unitPrice.amount < active.unitPrice.amount)) return NOT_CHEAPER;
  return { cheaper: true, activeUnitPrice: active.unitPrice, wowUnitPrice: wow.unitPrice };
}

/** The active side as the shared price store holds it (`usePriceEntry`): a priced entry, or
 *  `undefined` for anything else (not asked, loading, failed, unpriced). */
export function comparableFromPriceEntry(
  entry: PriceEntry | undefined,
): ComparableQuote | undefined {
  if (entry?.state !== 'priced') return undefined;
  return { status: 'priced', unitPrice: entry.unitPrice, kind: entry.kind };
}
